/***
 *
 *   ORGANIZATION
 *   Update the active organization information
 *
 **********/

import { Fragment, useContext, useEffect, useMemo, useState } from 'react';
import { AuthContext, ViewContext, Card, AccountNav, Animate, useNavigate } from 'components/lib';
import { Modal, Box, Button } from '@mui/material';
import { UMButton } from '@urbanmetrix/um-kit';
import {
  authClient,
  deleteOrganization,
  listOrganizations,
  updateOrganization,
  verifyCurrentUserPassword,
} from '../../lib/auth-client';
import { PendingPlanEntryDialog } from '../../components/pending-plan-entry-dialog/PendingPlanEntryDialog';
import {
  captureSubscriptionEntry,
  clearPendingSubscription,
  hasPendingSubscription,
  ORGANIZATION_PENDING_PLAN_MESSAGE,
} from '../../lib/pending-subscription';
import { CreateOrganizationModal } from '../add-organization/CreateOrganizationModal';
import { getApiErrorMessage } from 'api/apiClient';
import { SWITZERLAND, getCountryIso2, type CountryOption } from '../add-organization/countries';

const BILLING_COUNTRIES = [SWITZERLAND] as const;

type OrganizationRecord = {
  id: string | number;
  name?: string;
  billingAddressStreet?: string;
  billingAddressStreetNumber?: string;
  billingAddressPostalCode?: string;
  billingAddressCountry?: string;
};

type OrganizationActionError = {
  message?: string;
  error?: {
    message?: string;
    code?: string | number;
  };
  code?: string | number;
  status?: string | number;
};

type OrganizationFormState = {
  name: string;
  billingAddressStreet: string;
  billingAddressStreetNumber: string;
  billingAddressPostalCode: string;
  billingAddressCountry: string;
};

type OrganizationFormErrors = Partial<Record<keyof OrganizationFormState, string>>;

function validateForm(form: OrganizationFormState) {
  const nextErrors: OrganizationFormErrors = {};

  if (!form.name.trim()) {
    nextErrors.name = 'Please enter an organization name';
  }

  if (!form.billingAddressStreet.trim()) {
    nextErrors.billingAddressStreet = 'Street is required';
  }

  if (!form.billingAddressStreetNumber.trim()) {
    nextErrors.billingAddressStreetNumber = 'Street number is required';
  }

  if (!form.billingAddressPostalCode.trim()) {
    nextErrors.billingAddressPostalCode = 'Postal code is required';
  }

  if (!form.billingAddressCountry.trim()) {
    nextErrors.billingAddressCountry = 'Billing country is required';
  }

  return nextErrors;
}

function FieldError({ message }: { message?: string }) {
  if (!message) return null;

  return <span style={{ display: 'block', marginTop: '8px', color: '#dc2626', fontSize: '12px' }}>{message}</span>;
}

function TextField({
  id,
  label,
  value,
  onChange,
  error,
  disabled = false,
}: {
  id: string;
  label: string;
  value: string;
  onChange?: (value: string) => void;
  error?: string;
  disabled?: boolean;
}) {
  return (
    <div>
      <label htmlFor={id} style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500 }}>
        {label}
      </label>
      <input
        id={id}
        type="text"
        value={value}
        disabled={disabled}
        onChange={(event) => onChange?.(event.target.value)}
        style={{
          width: '100%',
          height: '40px',
          padding: '0 14px',
          border: error ? '1px solid #dc2626' : '0',
          borderRadius: '6px',
          background: disabled ? 'rgba(255, 255, 255, 0.22)' : 'rgba(255, 255, 255, 0.35)',
          color: disabled ? 'rgba(0, 0, 0, 0.72)' : 'inherit',
          cursor: disabled ? 'not-allowed' : 'text',
        }}
      />
      <FieldError message={error} />
    </div>
  );
}

function SelectField({
  id,
  label,
  value,
  onChange,
  error,
  options,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  options: readonly CountryOption[];
}) {
  return (
    <div>
      <label htmlFor={id} style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 500 }}>
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        style={{
          width: '100%',
          height: '40px',
          padding: '0 14px',
          border: error ? '1px solid #dc2626' : '0',
          borderRadius: '6px',
          background: 'rgba(255, 255, 255, 0.35)',
          appearance: 'auto',
        }}
      >
        {options.map((option) => (
          <option key={option.iso2} value={option.iso2}>
            {option.label}
          </option>
        ))}
      </select>
      <FieldError message={error} />
    </div>
  );
}

export function Organization() {
  const authContext = useContext(AuthContext) as any;
  const viewContext = useContext(ViewContext) as any;
  const navigate = useNavigate();

  const [organizations, setOrganizations] = useState<OrganizationRecord[]>([]);
  const [form, setForm] = useState<OrganizationFormState>({
    name: '',
    billingAddressStreet: '',
    billingAddressStreetNumber: '',
    billingAddressPostalCode: '',
    billingAddressCountry: SWITZERLAND.iso2,
  });
  const [errors, setErrors] = useState<OrganizationFormErrors>({});
  const [isSaving, setIsSaving] = useState(false);
  const [openCreateOrganizationModal, setOpenCreateOrganizationModal] = useState(false);
  const [openDeleteOrganizationModal, setOpenDeleteOrganizationModal] = useState(false);
  const [openPendingPlanDialog, setOpenPendingPlanDialog] = useState(false);
  const [isSubmittingAction, setIsSubmittingAction] = useState(false);
  const [deleteOrganizationAlsoAccount, setDeleteOrganizationAlsoAccount] = useState(false);
  const [deleteOrganizationPassword, setDeleteOrganizationPassword] = useState('');
  const [deleteOrganizationError, setDeleteOrganizationError] = useState('');

  useEffect(() => {
    const hasPendingEntry = captureSubscriptionEntry(window.location.search) || hasPendingSubscription();
    setOpenPendingPlanDialog(hasPendingEntry);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadOrganizations() {
      if (!authContext.isAuthenticated || !authContext.user?.session?.activeOrganizationId) {
        setOrganizations([]);
        return;
      }

      try {
        const nextOrganizations = await listOrganizations();
        if (!cancelled) {
          setOrganizations(Array.isArray(nextOrganizations) ? nextOrganizations : []);
        }
      } catch (_error) {
        if (!cancelled) {
          setOrganizations([]);
        }
      }
    }

    loadOrganizations();

    return () => {
      cancelled = true;
    };
  }, [authContext.isAuthenticated, authContext.user?.session?.activeOrganizationId]);

  const activeOrganizationId = authContext.user?.session?.activeOrganizationId;
  const isOwner = authContext.permission.owner;
  const currentOrganization = useMemo(
    () => organizations.find((organization) => String(organization.id) === String(activeOrganizationId)),
    [activeOrganizationId, organizations]
  );
  const currentOrganizationName = currentOrganization?.name ?? '';
  const organizationTargetText = currentOrganizationName ? `"${currentOrganizationName}"` : 'your current organization';
  useEffect(() => {
    setForm({
      name: currentOrganization?.name ?? '',
      billingAddressStreet: currentOrganization?.billingAddressStreet ?? '',
      billingAddressStreetNumber: currentOrganization?.billingAddressStreetNumber ?? '',
      billingAddressPostalCode: currentOrganization?.billingAddressPostalCode ?? '',
      billingAddressCountry: getCountryIso2(currentOrganization?.billingAddressCountry, SWITZERLAND.iso2),
    });
    setErrors({});
  }, [currentOrganization]);

  function handleFieldChange(name: keyof OrganizationFormState, value: string) {
    setForm((current) => ({
      ...current,
      [name]: value,
    }));
    setErrors((current) => ({
      ...current,
      [name]: '',
    }));
  }

  async function handleSave() {
    if (!activeOrganizationId || isSaving) {
      return;
    }

    const nextErrors = validateForm(form);
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    try {
      setIsSaving(true);
      await updateOrganization({
        name: form.name.trim(),
        billingAddressStreet: form.billingAddressStreet.trim(),
        billingAddressStreetNumber: form.billingAddressStreetNumber.trim(),
        billingAddressPostalCode: form.billingAddressPostalCode.trim(),
        billingAddressCountry: form.billingAddressCountry.trim(),
      });

      setOrganizations((prev) =>
        (prev ?? []).map((org) =>
          String(org.id) === String(activeOrganizationId)
            ? {
                ...org,
                name: form.name.trim(),
                billingAddressStreet: form.billingAddressStreet.trim(),
                billingAddressStreetNumber: form.billingAddressStreetNumber.trim(),
                billingAddressPostalCode: form.billingAddressPostalCode.trim(),
                billingAddressCountry: form.billingAddressCountry.trim(),
              }
            : org
        )
      );

      viewContext.notification.show('Organization updated', 'success', true, 'toast');
    } catch (error: any) {
      viewContext.notification.show(
        getApiErrorMessage(error, 'Unable to update your organization'),
        'error',
        true,
        'toast'
      );
    } finally {
      setIsSaving(false);
    }
  }

  function getDeleteAccountErrorMessage(
    error: OrganizationActionError,
    fallback = 'Unable to delete your organization and account. Please try again.'
  ): string {
    const errorMessage = getApiErrorMessage(error, '');
    const errorCode = error?.code || error?.error?.code || error?.status;
    const normalizedMessage = typeof errorMessage === 'string' ? errorMessage.toLowerCase() : '';
    const isWrongPassword =
      errorCode === 400 ||
      errorCode === 'INVALID_PASSWORD' ||
      normalizedMessage.includes('bad request') ||
      normalizedMessage.includes('invalid password') ||
      normalizedMessage.includes('wrong password') ||
      normalizedMessage.includes('password');

    return isWrongPassword ? 'Wrong password' : errorMessage || fallback;
  }

  function closeDeleteOrganizationModal(force = false) {
    if (isSubmittingAction && !force) {
      return;
    }

    setOpenDeleteOrganizationModal(false);
    setDeleteOrganizationAlsoAccount(false);
    setDeleteOrganizationPassword('');
    setDeleteOrganizationError('');
  }

  async function handleDeleteOrganization() {
    const organizationId = authContext.user?.session?.activeOrganizationId;

    if (!organizationId) {
      return;
    }

    try {
      setIsSubmittingAction(true);
      setDeleteOrganizationError('');

      if (!deleteOrganizationPassword) {
        setDeleteOrganizationError('Please enter your password.');
        setIsSubmittingAction(false);
        return;
      }

      await verifyCurrentUserPassword(deleteOrganizationPassword);
      await deleteOrganization(organizationId);
      closeDeleteOrganizationModal(true);

      if (deleteOrganizationAlsoAccount) {
        await authClient.deleteUser({
          password: deleteOrganizationPassword,
          fetchOptions: {
            throw: true,
          },
        });

        try {
          await authClient.signOut();
        } catch (_signOutError) {}
        window.location.assign('/login');
        return;
      }

      await authContext.refreshSession?.();
      navigate('/dashboard');
    } catch (error) {
      const actionError = error as OrganizationActionError;
      console.error(error);
      if (deleteOrganizationAlsoAccount) {
        setDeleteOrganizationError(getDeleteAccountErrorMessage(actionError));
      } else {
        viewContext.notification.show(
          getApiErrorMessage(actionError, 'An error occurred. Please try again.'),
          'error',
          true,
          'toast'
        );
      }
    } finally {
      setIsSubmittingAction(false);
    }
  }

  function handleClosePendingPlanDialog() {
    setOpenPendingPlanDialog(false);
    clearPendingSubscription();
  }

  function handleConfirmPendingPlanDialog() {
    handleClosePendingPlanDialog();
    setOpenCreateOrganizationModal(true);
  }

  return (
    <Fragment>
      <AccountNav />
      <Animate>
        <Card extraClass="form-container" isStat={true} style={{ padding: '24px 28px' }}>
          {!activeOrganizationId ? (
            <div>No active organization found.</div>
          ) : (
            <div style={{ width: '100%' }}>
              <div
                style={{
                  marginBottom: '18px',
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  gap: '16px',
                }}
              >
                {isOwner && activeOrganizationId && (
                  <UMButton
                    textOnly
                    onClick={() => {
                      setDeleteOrganizationAlsoAccount(false);
                      setDeleteOrganizationPassword('');
                      setDeleteOrganizationError('');
                      setOpenDeleteOrganizationModal(true);
                    }}
                    text="Delete Organization"
                  />
                )}
              </div>

              {isOwner ? (
                <Fragment>
                  <div
                    style={{
                      marginBottom: '18px',
                      display: 'grid',
                      gridTemplateColumns: 'minmax(320px, 420px)',
                      gap: '18px',
                      alignItems: 'end',
                    }}
                  >
                    <TextField
                      id="organization-name"
                      label="Organization Name"
                      value={form.name}
                      onChange={(value) => handleFieldChange('name', value)}
                      error={errors.name}
                    />
                  </div>

                  <div style={{ marginBottom: '18px' }}>
                    <p style={{ marginBottom: '12px', fontWeight: 700, fontSize: '16px' }}>Billing address</p>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns:
                          'minmax(280px, 1.8fr) minmax(140px, 0.9fr) minmax(140px, 0.9fr) minmax(220px, 1.1fr)',
                        gap: '14px',
                        alignItems: 'start',
                      }}
                    >
                      <TextField
                        id="billing-street"
                        label="Street"
                        value={form.billingAddressStreet}
                        onChange={(value) => handleFieldChange('billingAddressStreet', value)}
                        error={errors.billingAddressStreet}
                      />
                      <TextField
                        id="billing-street-number"
                        label="Street number"
                        value={form.billingAddressStreetNumber}
                        onChange={(value) => handleFieldChange('billingAddressStreetNumber', value)}
                        error={errors.billingAddressStreetNumber}
                      />
                      <TextField
                        id="billing-postal-code"
                        label="Postal code"
                        value={form.billingAddressPostalCode}
                        onChange={(value) => handleFieldChange('billingAddressPostalCode', value)}
                        error={errors.billingAddressPostalCode}
                      />
                      <SelectField
                        id="billing-country"
                        label="Country"
                        value={form.billingAddressCountry}
                        onChange={(value) => handleFieldChange('billingAddressCountry', value)}
                        error={errors.billingAddressCountry}
                        options={BILLING_COUNTRIES}
                      />
                    </div>
                  </div>

                  <div
                    style={{
                      marginTop: '1rem',
                      display: 'flex',
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                      gap: '12px',
                    }}
                  >
                    <UMButton text={isSaving ? 'Saving...' : 'Save'} onClick={handleSave} disabled={isSaving} />
                  </div>
                </Fragment>
              ) : (
                <div
                  style={{
                    marginBottom: '18px',
                    display: 'grid',
                    gridTemplateColumns: 'minmax(320px, 420px)',
                    gap: '18px',
                    alignItems: 'end',
                  }}
                >
                  <div style={{ fontSize: '15px', lineHeight: 1.5, paddingBottom: '8px' }}>
                    <strong>Organization Name:</strong> {currentOrganizationName || '-'}
                  </div>
                </div>
              )}
            </div>
          )}
        </Card>
      </Animate>
      <CreateOrganizationModal
        open={openCreateOrganizationModal}
        onClose={() => setOpenCreateOrganizationModal(false)}
        previousOrganizations={organizations}
        onCreated={(nextOrganizations) => setOrganizations(nextOrganizations)}
        redirectTo="/register/plan"
      />

      <Modal className="custom-modal" open={openDeleteOrganizationModal} onClose={() => closeDeleteOrganizationModal()}>
        <Box className="organization-action-modal">
          <div className="organization-action-modal__header">
            <div className="organization-action-modal__title">Delete Organization</div>
            <div className="organization-action-modal__subtitle">
              {`Are you sure you want to delete ${organizationTargetText}, which is your current organization? This action cannot be undone.`}
            </div>
          </div>

          <div className="organization-action-modal__field">
            {/*
              // to-do apis delete-user not implemented on backend side
              <label className="organization-action-modal__checkbox">
              <input
                type="checkbox"
                checked={deleteOrganizationAlsoAccount}
                onChange={(event) => {
                  const isChecked = event.target.checked;
                  setDeleteOrganizationAlsoAccount(isChecked);
                  setDeleteOrganizationError('');
                  if (!isChecked) {
                    setDeleteOrganizationPassword('');
                  }
                }}
                disabled={isSubmittingAction}
              />
              <span>Delete also my account</span>
              </label>
            */}

            <div className="organization-action-modal__nested-field">
              <label className="organization-action-modal__label" htmlFor="delete-organization-account-password">
                Password
              </label>
              <input
                id="delete-organization-account-password"
                type="password"
                className="organization-action-modal__input"
                value={deleteOrganizationPassword}
                onChange={(event) => {
                  setDeleteOrganizationPassword(event.target.value);
                  if (deleteOrganizationError) {
                    setDeleteOrganizationError('');
                  }
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    handleDeleteOrganization();
                  }
                }}
                disabled={isSubmittingAction}
              />
            </div>

            {deleteOrganizationError && (
              <div className="organization-action-modal__error">{deleteOrganizationError}</div>
            )}
          </div>

          <div className="organization-action-modal__actions">
            <Button
              variant="contained"
              className="modal-no-btn organization-action-modal__cancel"
              onClick={() => closeDeleteOrganizationModal()}
              disabled={isSubmittingAction}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              className="modal-yes-btn organization-action-modal__confirm"
              onClick={handleDeleteOrganization}
              disabled={isSubmittingAction}
            >
              Delete
            </Button>
          </div>
        </Box>
      </Modal>
      <PendingPlanEntryDialog
        open={openPendingPlanDialog}
        title="Create a new organization"
        message={ORGANIZATION_PENDING_PLAN_MESSAGE}
        onClose={handleClosePendingPlanDialog}
        onConfirm={handleConfirmPendingPlanDialog}
        confirmLabel="CREATE"
        cancelLabel="CANCEL"
      />
    </Fragment>
  );
}
